import { prepareData, basicTransformer } from 'utils/googleData'
import cntl from 'cntl'
import React, { useEffect, useState } from 'react'
import { useActions, useValues } from 'kea'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { pricingSliderLogic } from 'components/Pricing/PricingSlider/pricingSliderLogic'
import PricingHeader from './PricingHeader'
import PricingSliderSection from './PricingSliderSection'
import DiscountSection from './DiscountSection'
import { PropertyManagement, RealestateCrm, RealestateMarketing, MobileApps, Websites } from 'components/ProductIcons'
import { LogSlider } from 'components/Pricing/PricingSlider/LogSlider'

export const section = cntl`
    max-w-6xl
    xl:max-w-7xl
    mx-auto
    px-4
`

export const PricingCalculator = (props) => {
    const { i18n } = useTranslation()
    const result = prepareData({ props })
    const { enable, title, text, text2, text3, text4, direction, editions } = result
    const discounts = basicTransformer(result.discounts, i18n)
    const packages = basicTransformer(result.packages, i18n)

    const {
        monthlyTotal,
        finalAnnualCost,
        finalMonthlyCost,
        monthlyStandardTotal,
        //value
        propertymanagementcloudCost,
        realestateCrmCost,
        marketingContactsCost,
        marketingWebsiteCost,
        marketingAppCost,
        //slidervalue
        propertyManagemenSliderValue,
        realestateCrmSliderValue,
        marketingContactsSliderValue,
        marketingWebsiteSliderValue,
        marketingAppSliderValue,
        //users
        marketingContactsUserNumber,
        marketingWebsiteUserNumber,
        marketingAppUserNumber,
        realestateCrmUserNumber,
        propertyManagementUnitNumber,
    } = useValues(pricingSliderLogic)

    const {
        //setSlidervalue
        setRealestateCrmSliderValue,
        setMarketingContactsSliderValue,
        setMarketingWebsiteSliderValue,
        setMarketingAppSliderValue,
        setPropertyManagemenSliderValue,
        //setPricingOption
        setPricingOption,
        setCrmPricingOption,
        setMarketingPricingOption,
        setMarketingContactsPricingOption,
        setMarketingWebsitePricingOption,
        setMarketingAppPricingOption,
    } = useActions(pricingSliderLogic)

    const [marketingContactsEnabled, setMarketingContactsEnabled] = useState(true)
    const [marketingWebsiteEnabled, setMarketingWebsiteEnabled] = useState(true)
    const [marketingAppEnabled, setMarketingAppEnabled] = useState(true)
    const [realestateCrmEnabled, setRealestateCrmEnabled] = useState(true)
    const [propertyManagementEnabled, setPropertyManagementEnabled] = useState(true)
    const [professionalMode, setProfessionalMode] = useState(false)
    const handleProfessionalModeChange = (checked: boolean) => {
        setPricingOption(checked ? 'propertymanagementcloud-professional' : 'propertymanagementcloud-standard')
        setCrmPricingOption(checked ? 'real-estate-crm-cloud-professional' : 'real-estate-crm-cloud-standard')
        setMarketingPricingOption(
            checked ? 'real-estate-marketing-cloud-professional' : 'real-estate-marketing-cloud-standard'
        )
        setMarketingContactsPricingOption(
            checked ? 'marketing-contacts-cloud-professional' : 'marketing-contacts-cloud-standard'
        )
        setMarketingWebsitePricingOption(
            checked ? 'marketing-website-cloud-professional' : 'marketing-website-cloud-standard'
        )
        setMarketingAppPricingOption(checked ? 'marketing-app-cloud-professional' : 'marketing-app-cloud-standard')
        setProfessionalMode(checked)
    }

    useEffect(() => {
        setPropertyManagemenSliderValue(3) // Adjust initial value if necessary
        setRealestateCrmSliderValue(1) // Adjust initial value if necessary
        setMarketingContactsSliderValue(8) // Adjust initial value if necessary
        setMarketingWebsiteSliderValue(5) // Set initial value to 0
        setMarketingAppSliderValue(6) // Set initial value to 0
    }, [])

    if (enable?.toLowerCase?.() === 'false' && !process.env.RENDER_DISABLED_COMPONENTS) {
        return <></>
    }

    return (
        <section dir={direction} className={`${section} mb-12`}>
            <PricingHeader
                title={title}
                text={text}
                text2={text2}
                text3={text3}
                text4={text4}
                direction={direction}
                editions={editions}
                professionalMode={professionalMode}
                handleProfessionalModeChange={handleProfessionalModeChange}
            />

            <div className="grid gap-8 lg:grid-cols-3 xl:gap-12">
                <div className="col-span-2">
                    <h4 className="mb-3">{text}</h4>

                    <div className="grid grid-cols-4 rounded-md bg-gray-accent-light dark:bg-dark">
                        <PricingSliderSection
                            title={packages?.options?.[0]?.title}
                            value={propertyManagemenSliderValue}
                            setValue={setPropertyManagemenSliderValue}
                            cost={propertymanagementcloudCost}
                            userNumber={propertyManagementUnitNumber}
                            icon={<PropertyManagement />}
                            min={10}
                            max={5000}
                            stepsInRange={100}
                            marks={[10, 50, 100, 1000, 5000]}
                            sliderComponent={LogSlider}
                            disabled={!propertyManagementEnabled}
                        />

                        <PricingSliderSection
                            title={packages?.options?.[1]?.title}
                            value={realestateCrmSliderValue}
                            setValue={setRealestateCrmSliderValue}
                            cost={realestateCrmCost}
                            userNumber={realestateCrmUserNumber}
                            icon={<RealestateCrm />}
                            min={1}
                            max={1000}
                            stepsInRange={100}
                            marks={[1, 5, 10, 50, 100, 500, 1000]}
                            sliderComponent={LogSlider}
                            disabled={!realestateCrmEnabled}
                        />

                        <div className="col-span-4 lg:col-span-3">
                            <div className="rounded-md border bg-gray-accent-light p-4 dark:bg-dark">
                                <h4 className="mb-3">Real Estate Marketing</h4>
                                <PricingSliderSection
                                    title="Marketing Automation Contacts"
                                    value={marketingContactsSliderValue}
                                    setValue={setMarketingContactsSliderValue}
                                    cost={marketingContactsCost}
                                    userNumber={marketingContactsUserNumber}
                                    icon={<RealestateMarketing />}
                                    min={1000}
                                    max={100000}
                                    stepsInRange={1000}
                                    marks={[0, 1000, 5000, 10000, 50000, 100000]}
                                    sliderComponent={LogSlider}
                                    disabled={!marketingContactsEnabled}
                                />

                                <PricingSliderSection
                                    title="Website Monthly Active Users"
                                    value={marketingWebsiteSliderValue}
                                    setValue={setMarketingWebsiteSliderValue}
                                    cost={marketingWebsiteCost}
                                    userNumber={marketingWebsiteUserNumber}
                                    icon={<Websites />}
                                    min={100}
                                    max={10000}
                                    stepsInRange={100}
                                    marks={[0, 100, 500, 1000, 5000, 10000]}
                                    sliderComponent={LogSlider}
                                    disabled={!marketingWebsiteEnabled}
                                />
                                <PricingSliderSection
                                    title="Mobile App Monthly Active Users"
                                    value={marketingAppSliderValue}
                                    setValue={setMarketingAppSliderValue}
                                    cost={marketingAppCost}
                                    userNumber={marketingAppUserNumber}
                                    icon={<MobileApps />}
                                    min={100}
                                    max={10000}
                                    stepsInRange={100}
                                    marks={[0, 100, 500, 1000, 5000, 10000]}
                                    sliderComponent={LogSlider}
                                    disabled={!marketingAppEnabled}
                                />
                            </div>
                        </div>

                        <div className="col-span-3 p-4">
                            <strong>{text2}</strong>
                            <br />
                            <p className="mb-0 text-sm opacity-60">{text3}</p>
                        </div>
                        <div className="p-4 text-center">
                            <span className="text-lg font-bold">${monthlyTotal.toLocaleString()}</span>
                            <span className="opacity-60">{text4}</span>
                        </div>
                    </div>
                </div>
                <DiscountSection discounts={discounts} />
            </div>
        </section>
    )
}

export default PricingCalculator
